import Component from "@glimmer/component";
import { service } from "@ember/service";
import { registerDestructor } from "@ember/destroyable";
import type Owner from "@ember/owner";
import type RouterService from "@ember/routing/router-service";
import type SequencerService from "#services/sequencer.ts";
import type { PlaySongRouteModel } from "#app/routes/play/song.ts";
import { serializeSong } from "#app/utils/serialization.ts";
import config from "#config";
import Player from "#components/player.gts";

export interface PlaySongTemplateSignature {
  Args: {
    model: PlaySongRouteModel;
  };
}

export const URL_SYNC_DEBOUNCE_MS = 150;

/**
 * Route template component for `/play/:song_data`.
 *
 * Responsibilities:
 * - Mounts the interactive Player sequencer.
 * - Synchronizes song changes back to the URL unidirectionally via debounced replaceState.
 */
export default class PlaySongTemplateComponent extends Component<PlaySongTemplateSignature> {
  @service declare sequencer: SequencerService;
  @service declare router: RouterService;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(owner: Owner, args: PlaySongTemplateSignature["Args"]) {
    super(owner, args);

    registerDestructor(this, () => {
      this.cleanup();
    });
  }

  /**
   * Invoked whenever the player triggers an update (step toggles, tempo changes,
   * channel additions/deletions, sound cycling, or preset selection).
   */
  handleSongUpdated = (): void => {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.syncUrl();
    }, URL_SYNC_DEBOUNCE_MS);
  };

  /**
   * Serializes the current song state and synchronizes the browser address bar.
   *
   * NOTE:
   * We purposely use `window.history.replaceState` together with `router.urlFor`
   * instead of `router.replaceWith` because:
   * 1. The URL is only consumed once on page load (one-way data flow).
   * 2. Direct `replaceState` avoids triggering Ember's route model hooks and transition
   *    lifecycle cycles on rapid user edits.
   * 3. It avoids transition-abort warnings in the console when users toggle steps rapidly.
   */
  syncUrl = (): void => {
    if (config.locationType === "none") {
      return;
    }

    const currentSong = this.sequencer.song;
    if (!currentSong) {
      return;
    }

    try {
      const serialized = serializeSong(currentSong);
      const url = this.router.urlFor("play.song", serialized);
      window.history.replaceState(window.history.state, "", url);
    } catch (error) {
      console.error("[template:play.song] error serializing song for URL sync", error);
    }
  };

  private cleanup(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  <template>
    <div class="max-w-5xl mx-auto p-4 bg-slate-900" data-test-play-song-page>
      <Player @onSongUpdated={{this.handleSongUpdated}} />
    </div>
  </template>
}
